import { Component, effect, ElementRef, inject, OnDestroy, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ChatBotScriptService } from 'src/app/services';
import { VideoLoaderComponent, SuggestionsComponent, InfoAndChatComponent, SayGoodByeViewComponent } from "./components";
import { WebsocketLaravelReverb } from './services';
import { CHANNEL_ATRIL_PANTALLA_NAME, END_EXPERIENCE_EVENT, GO_TO_HOME_PAGE_EVENT, GO_TO_NORMAL_OR_SATURED_DETAIL_PAGE_EVENT, SHOW_HOME_PAGE_EVENT, START_IMMERSIVE_MODE_EVENT } from './config';
import { CustomRouteService } from 'src/app/pages/custom-route/services';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    RouterOutlet,
    VideoLoaderComponent,
    SuggestionsComponent,
    InfoAndChatComponent,
    SayGoodByeViewComponent
],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export default class HomeComponent implements OnDestroy {
  public videoLoaderRef = viewChild('videoLoaderRef', { read: ElementRef });
  public immersiveVideoRef = viewChild<ElementRef<HTMLVideoElement>>('immersiveVideoRef');
  
  private _router = inject(Router);
  private _chatbotScriptService = inject(ChatBotScriptService);
  private _websocketLaravelReverbService = inject(WebsocketLaravelReverb);
  private _customRouteService = inject(CustomRouteService);
  
  protected chatId = this._chatbotScriptService.chatId;
  private _message = this._websocketLaravelReverbService.messages;
  protected routeToken = this._customRouteService.token;
  
  protected srcImmersiveVideo = signal<string | null>(null);
  protected immersiveModeActived = signal<boolean>(false);
  
  private _scaleUpTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _videoPosterTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _hideImmersiveVideoTimeoutId: ReturnType<typeof setTimeout> | null = null;
  
  private _audioPlayer: HTMLAudioElement | null = null;
  
  constructor() {
    this._subscribeToChannelGetSrcImmersiveVideo();
  }
  
  checkMessageWebSocket = effect(() => {
    const messages = this._message();
    console.log('New WebSocket Message:', messages);
  }, { allowSignalWrites: true });
  
  setFadeOutEffect = effect(() => {
    const ref = this.videoLoaderRef();
    if (ref) {
      const videoLoaderEl = ref.nativeElement as HTMLVideoElement;
      
      this._subscribeToWebsocketChannelShowHome(videoLoaderEl);
    }
  });
  
  navigateToDetailsByWebSockets = effect(() => {
    this._subscribeToWebsocketChannelsNavigation();
  }, { allowSignalWrites: true });
  
  getSrcImmersiveMode = effect(() => {
    this._subscribeToChannelGetSrcImmersiveVideo();
  })
  
  startImmersiveVideo = effect(() => {
    const src = this.srcImmersiveVideo();
    if(!src) return;
    
    this.immersiveModeActived.set(true);
    
    this._playVideo();
  }, { allowSignalWrites: true });
  
  endExperience = effect(() => {
    const token = this.routeToken();
    if(!token) return;
    
    this._endExperienceSubscription(token);
  }, { allowSignalWrites: true });
  
  private _subscribeToWebsocketChannelsNavigation(): void {
    this._websocketLaravelReverbService
      .subscribeToChannel(
        CHANNEL_ATRIL_PANTALLA_NAME, 
        GO_TO_NORMAL_OR_SATURED_DETAIL_PAGE_EVENT + this.routeToken()
      )
      .subscribe({
        next: (data: any) => {
          const typeNormalized = data[0].type !== 'event' ? 'point' : 'event';
          const pointId = data[0].id;
          if(!typeNormalized || !pointId) return;
          
          this._router.navigate(
            ['/home', typeNormalized, pointId]
          );
        },
        error: (err: any) => console.error('Error en canal:', err)
      });
    
    this._websocketLaravelReverbService
      .subscribeToChannel(
        CHANNEL_ATRIL_PANTALLA_NAME, 
        GO_TO_HOME_PAGE_EVENT + this.routeToken()
      )
      .subscribe({
        next: (_data: any) => {
          this._router.navigate(
            ['/home/route', this.routeToken()],
          );
        },
        error: (err: any) => console.error('Error en canal:', err)
    });
  }
  
  private _subscribeToChannelGetSrcImmersiveVideo(): void {
    this._websocketLaravelReverbService
      .subscribeToChannel(CHANNEL_ATRIL_PANTALLA_NAME, START_IMMERSIVE_MODE_EVENT + this.routeToken())
    .subscribe({
      next: (data: any) => {
        // Clean up previous video if any
        this.srcImmersiveVideo.set(null);
        
        const typeNormalized = data[0].type !== 'event' ? 'point' : 'event';
        const pointId = data[0].id;
        if(!typeNormalized || !pointId) return;
        
        setTimeout(() => {
          this._stopAudio(); // Stop any playing audio when immersive video starts
          this.srcImmersiveVideo.set(`/assets/fitur-2026/immersive/${typeNormalized}/${pointId}.mp4`);    
        }, 200); // small delay to ensure video element reloads    
      },
      error: (err: any) => console.error('Error en canal:', err)
    });
  }
  
  private _playVideo(): void {
    const immersiveVideoRef = this.immersiveVideoRef();
    if (!immersiveVideoRef) return;
    const immersiveVideoEl = immersiveVideoRef.nativeElement;
  
    const playPromise = immersiveVideoEl.play();
    if (playPromise !== undefined) {
      playPromise.then(_ => {})
      .catch(error => {
        console.error('Error attempting to play', error);
      });
    }
    
    // cleanup previous class to initialize animation
    immersiveVideoEl.classList.remove('scale-up', 'push-back', 'fade-in');
    
    immersiveVideoEl.classList.add('fade-in');
      
    this._scaleUpTimeoutId = setTimeout(() => {
      immersiveVideoEl.playsInline = true;
      immersiveVideoEl.classList.add('scale-up');
    }, 100);

    this._videoPosterTimeoutId = setTimeout(() => {
      this.immersiveModeActived.set(false);
      
      immersiveVideoEl.classList.remove('fade-in');
      immersiveVideoEl.classList.add('push-back');
    }, 12000); // ! TIEMPO IGUAL QUE EN ATRIL 
  }
  
  getAnimationState(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }
  
  private _subscribeToWebsocketChannelShowHome(videoLoaderEl: HTMLVideoElement): void {
    this._websocketLaravelReverbService
      .subscribeToChannel(
        CHANNEL_ATRIL_PANTALLA_NAME,
         SHOW_HOME_PAGE_EVENT + this.routeToken()
      ).subscribe({
        next: (_data: any) => {
          videoLoaderEl.classList.add('fade-out');
          this._playAudio('/assets/fitur-2026/audios/home-bg.mp3');
        },
        error: (err: any) => console.error('Error en canal:', err)
    });
  }
  
  private _playAudio(src: string): void {
    this._stopAudio();
    this._audioPlayer = new Audio(src);
    this._audioPlayer.loop = true;
    this._audioPlayer.volume = 0; // Empieza en silencio

    this._audioPlayer.play().then(() => {
      const targetVolume = 0.2;
      const fadeDuration = 800; // ms
      const steps = 20;
      const stepTime = fadeDuration / steps;
      let currentStep = 0;

      const fadeIn = () => {
        currentStep++;
        const newVolume = targetVolume * (currentStep / steps);
        this._audioPlayer!.volume = Math.min(newVolume, targetVolume);
        if (currentStep < steps) {
          setTimeout(fadeIn, stepTime);
        }
      };

      fadeIn();
    }).catch(err => {
      console.error('Error reproduciendo audio:', err);
    });
  }
  
  private _stopAudio(): void {
    if (this._audioPlayer) {
      const fadeDuration = 800; // ms
      const steps = 20;
      const stepTime = fadeDuration / steps;
      const initialVolume = this._audioPlayer.volume;
      let currentStep = 0;

      const fade = () => {
        currentStep++;
        const newVolume = initialVolume * (1 - currentStep / steps);
        this._audioPlayer!.volume = Math.max(newVolume, 0);
        if (currentStep < steps) {
          setTimeout(fade, stepTime);
        } else {
          this._audioPlayer!.pause();
          this._audioPlayer!.currentTime = 0;
          this._audioPlayer = null;
        }
      };

      fade();
    }
  }
  
  private _endExperienceSubscription(token: string): void {
    this._websocketLaravelReverbService
    .subscribeToChannel(
      CHANNEL_ATRIL_PANTALLA_NAME,
      END_EXPERIENCE_EVENT + token
    ).subscribe({
      next: (_data: any) => {
        this._router.navigate(['/']);
      },
      error: (err: any) => console.error('Error en canal:', err)
    });
  }
  
  ngOnDestroy(): void {
    if (this._scaleUpTimeoutId) {
      clearTimeout(this._scaleUpTimeoutId);
    }
    if (this._videoPosterTimeoutId) {
      clearTimeout(this._videoPosterTimeoutId);
    }
    if (this._hideImmersiveVideoTimeoutId) {
      clearTimeout(this._hideImmersiveVideoTimeoutId);
    }
  }
}
